/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Proforma } from '../../lib/api/proformas.js';
import { useProformas } from '../../lib/queries/proformas.js';
import { formatIst } from '../../lib/utils/datetime.js';
import { formatInr } from '../../lib/utils/money.js';

type Tab = 'all' | 'draft' | 'sent' | 'approved';
const TABS: readonly Tab[] = ['all', 'draft', 'sent', 'approved'];
const STATUS_TONE: Readonly<Record<string, BadgeTone>> = {
  draft: 'slate',
  sent: 'violet',
  approved: 'green',
  declined: 'rose',
  converted: 'slate',
};
const COLS = ['id', 'patient', 'sessions', 'amount', 'month', 'status'] as const;

function ProformaRow({
  proforma,
  rowActions,
}: {
  readonly proforma: Proforma;
  readonly rowActions?: ProformaRowActions | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">
        {proforma.proformaNumber}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{proforma.clientName}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{proforma.sessionsCount}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {formatInr(proforma.totalPaise)}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {formatIst(`${proforma.periodStart}T00:00:00.000Z`, 'MMM yyyy')}
      </td>
      <td className="px-5 py-3">
        <Badge
          label={t(`proforma.status.${proforma.status}`)}
          tone={STATUS_TONE[proforma.status] ?? 'slate'}
        />
      </td>
      {rowActions !== undefined && (
        <td className="px-5 py-3 text-right">
          <div className="flex justify-end gap-2">{rowActions(proforma)}</div>
        </td>
      )}
    </tr>
  );
}

/** Optional per-row action renderer (admin Send/Convert); omit for a read-only table. */
export type ProformaRowActions = (proforma: Proforma) => JSX.Element | null;

/** Proforma pipeline table with All/Draft/Sent/Approved tabs (Figma Finance). */
export function FinanceProformaTable({
  rowActions,
}: {
  readonly rowActions?: ProformaRowActions;
}): JSX.Element {
  const { t } = useTranslation();
  const proformas = useProformas();
  const [tab, setTab] = useState<Tab>('all');
  const select = useCallback(
    (next: Tab) => () => {
      setTab(next);
    },
    [],
  );
  const all = proformas.data ?? [];
  const rows = tab === 'all' ? all : all.filter((p) => p.status === tab);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="flex gap-1 border-b border-slate-100 p-2 dark:border-slate-800">
        {TABS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={select(value)}
            className={`focus-ring rounded-lg px-4 py-1.5 text-sm font-medium ${
              tab === value ? 'bg-[var(--accent)] text-white' : 'text-slate-600 dark:text-slate-300'
            }`}
          >
            {t(`finance.proformas.tab_${value}`)}
          </button>
        ))}
      </div>
      <QueryState
        isLoading={proformas.isLoading}
        isError={proformas.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('finance.proformas.empty')}
      >
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
            <tr>
              {COLS.map((c) => (
                <th key={c} className="px-5 py-3 font-semibold">
                  {t(`finance.proformas.col.${c}`)}
                </th>
              ))}
              {rowActions !== undefined && <th className="px-5 py-3" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((p) => (
              <ProformaRow key={p.id} proforma={p} rowActions={rowActions} />
            ))}
          </tbody>
        </table>
      </QueryState>
    </div>
  );
}
